// ================================================================
// src/pages/teacher/TeacherFreeStudy.jsx
// ================================================================
// پنل معلم برای ساخت و مدیریت دوره‌های مطالعه آزاد
// ================================================================

import React, {
  useState, useEffect, useMemo, useCallback, useRef,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import './teacher-books.css';
import '../../components/books/books-shared.css';

import CircularProgress from '../../core/books/CircularProgress'; // 🆕 اضافه شود

import {
  engine,
  BooksEngineProvider,
  useBooksEngine, useEngineReady,
  useTeacherByUserId, useMyFreeStudyProjects, useFreeStudyEditor,
  useVersionActions, useProjectActions,
  useImageUpload, useKeyboardShortcuts, useBeforeUnload,
  PROJECT_STATE, VERSION_STATUS,
} from '../../hooks/useBooksEngine.js';

import dataEngine from '../../core/books/data.js';
import { formatPrice, toPersianDigits } from '../../freeStudy/core.js';
import { resolveImageUrl } from '../../data/freeStudyData.js';

import {
  Spinner, Badge, Chip, EmptyState, SkeletonLoader,
  Modal, ConfirmModal,
  AutoSaveIndicator, ProjectStateBadge,
} from '../../components/books/BooksSharedUI.jsx';

// 🆕 انتخاب‌گر دسته‌بندی
import { CategoryPickerModal } from '../../components/books/CategoryPickerModal.jsx';



// 🆕 آیا آپلود تصویر بندانگشتی برای ویدیوها فعال باشد؟
const SHOW_THUMBNAIL_UPLOAD = false;
// ================================================================
// MAIN ENTRY
// ================================================================
export default function TeacherFreeStudyPage() {
  const { user } = useAuth();
  if (!user || user.role !== 'teacher') return <AccessDenied />;
  return (
    <BooksEngineProvider user={user}>
      <Inner />
    </BooksEngineProvider>
  );
}

function AccessDenied() {
  const navigate = useNavigate();
  return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 64, marginBottom: 20 }}>🔒</div>
      <h2>دسترسی محدود</h2>
      <p>این صفحه مخصوص معلمان است.</p>
      <button className="tb-btn tb-btn-primary" onClick={() => navigate('/TeacherHome')}>
        بازگشت
      </button>
    </div>
  );
}

// ================================================================
// INNER
// ================================================================
function Inner() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useBooksEngine();
  const { ready } = useEngineReady();

  const editId = searchParams.get('edit');
  const isCreating = editId === 'new';

  const teacherProfile = useTeacherByUserId(user?.id);

  // 🌗 تم مشترک
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try { return engine.getRaw('navbar2-drawer-dark') === '1'; }
    catch { return false; }
  });

  useEffect(() => {
    try { engine.setRaw('navbar2-drawer-dark', isDarkMode ? '1' : '0'); }
    catch {}
  }, [isDarkMode]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'navbar2-drawer-dark') setIsDarkMode(e.newValue === '1');
    };
    window.addEventListener('storage', handler);
    const interval = setInterval(() => {
      try {
        const current = engine.getRaw('navbar2-drawer-dark') === '1';
        setIsDarkMode((prev) => (prev !== current ? current : prev));
      } catch {}
    }, 500);
    return () => {
      window.removeEventListener('storage', handler);
      clearInterval(interval);
    };
  }, []);

  if (!ready) {
    return (
      <div className={`tb-page ${isDarkMode ? 'dark-mode' : ''}`}>
        <Background />
        <main className="tb-main" style={{ padding: 24 }}>
          <SkeletonLoader variant="card" count={3} />
        </main>
      </div>
    );
  }

  if (!teacherProfile) {
    return (
      <div className={`tb-page ${isDarkMode ? 'dark-mode' : ''}`} style={{ padding: 40 }}>
        <EmptyState
          icon="🎓"
          title="ابتدا باید معلم تأییدشده باشید"
          description="برای ساخت دوره‌ی مطالعه آزاد، ابتدا درخواست معلمی خود را از پنل کتاب‌ها ثبت کنید."
          action={
            <button className="tb-btn tb-btn-primary" onClick={() => navigate('/teacher/books')}>
              رفتن به پنل کتاب‌ها
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className={`tb-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <Background />
      {editId ? (
        <FreeStudyEditor
          projectId={isCreating ? null : editId}
          isNew={isCreating}
          teacherProfile={teacherProfile}
          onExit={() => setSearchParams({})}
        />
      ) : (
        <FreeStudyList
          teacherProfile={teacherProfile}
          onEdit={(id) => setSearchParams({ edit: id })}
          onCreate={() => setSearchParams({ edit: 'new' })}
        />
      )}
    </div>
  );
}

function Background() {
  return (
    <div className="tb-bg" aria-hidden="true">
      <div className="tb-bg-orb tb-bg-orb-1" />
      <div className="tb-bg-orb tb-bg-orb-2" />
      <div className="tb-bg-orb tb-bg-orb-3" />
      <div className="tb-bg-grid" />
    </div>
  );
}

// ================================================================
// LIST
// ================================================================
function FreeStudyList({ teacherProfile, onEdit, onCreate }) {
  const navigate = useNavigate();
  const projects = useMyFreeStudyProjects();
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { hardDelete } = useProjectActions();

  const stats = useMemo(() => ({
    total: projects.length,
    building: projects.filter((p) => p.state === PROJECT_STATE.BUILDING).length,
    pending: projects.filter((p) =>
      [PROJECT_STATE.PENDING_INITIAL, PROJECT_STATE.UPDATE_PENDING].includes(p.state)
    ).length,
    published: projects.filter((p) => p.state === PROJECT_STATE.PUBLISHED).length,
  }), [projects]);

  const handleDelete = useCallback(async () => {
    if (!confirmDelete) return;
    await hardDelete.run(confirmDelete.id);
    setConfirmDelete(null);
  }, [confirmDelete, hardDelete]);

  return (
    <>
      <header className="tb-header">
        <div className="tb-header-inner">
          <div className="tb-header-id">
            <div className="tb-avatar" style={{ fontSize: 28 }}>🎬</div>
            <div className="tb-header-info">
              <h1 className="tb-header-name">مطالعه آزاد — پنل معلم 👋</h1>
              <div className="tb-header-meta">
                <Chip tone="purple">🎬 دوره‌های ویدیویی</Chip>
              </div>
            </div>
            <button
              type="button"
              className="tb-theme-toggle"
              onClick={() => navigate('/books')}
              title="بازگشت به پنل کتاب‌ها"
              aria-label="بازگشت"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
          <div className="tb-header-actions">
            {/* 🆕 دکمه بازگشت — استایل دایره‌ای و مناسب موبایل */}

            <button
              className="tb-btn tb-btn-primary"
              onClick={onCreate}
            >
              ➕ ساخت دوره‌ی جدید
            </button>
          </div>
        </div>

        <div className="tb-quick-stats">
          <QuickStat icon="📚" value={stats.total} label="کل دوره‌ها" tone="neutral" />
          <QuickStat icon="✏️" value={stats.building} label="در حال ساخت" tone="info" />
          <QuickStat icon="⏳" value={stats.pending} label="در انتظار بررسی" tone="warning" />
          <QuickStat icon="✅" value={stats.published} label="منتشرشده" tone="success" />
        </div>
      </header>

      <main className="tb-main">
        {projects.length === 0 ? (
          <div style={{ padding: 40 }}>
            <EmptyState
              icon="🎬"
              title="هنوز دوره‌ای نساخته‌اید"
              description="با ساخت اولین دوره‌ی مطالعه آزاد، درآمدزایی از محتوای خود را شروع کنید."
              action={
                <button className="tb-btn tb-btn-primary tb-btn-lg" onClick={onCreate}>
                  ➕ ساخت اولین دوره
                </button>
              }
            />
          </div>
        ) : (
          <div className="tb-projects-grid">
            {projects.map((p) => (
              <ProjectCard
                key={p.id}
                project={p}
                onEdit={() => onEdit(p.id)}
                onPreview={() => navigate(`/free-study/${p.id}`)}
                onDelete={() => setConfirmDelete(p)}
              />
            ))}
          </div>
        )}
      </main>

      <ConfirmModal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="حذف دوره"
        message={
          confirmDelete
            ? `آیا از حذف دائمی دوره‌ی «${confirmDelete.title}» مطمئن هستید؟ تمام ویدیوها برای همیشه حذف می‌شوند.`
            : ''
        }
        confirmText="🗑️ حذف دائمی"
        tone="danger"
        loading={hardDelete.loading}
      />
    </>
  );
}

function QuickStat({ icon, value, label, tone }) {
  return (
    <div className={`tb-stat tb-stat-${tone}`}>
      <span className="tb-stat-icon">{icon}</span>
      <div className="tb-stat-body">
        <span className="tb-stat-value">{value}</span>
        <span className="tb-stat-label">{label}</span>
      </div>
    </div>
  );
}

function ProjectCard({ project, onEdit, onPreview, onDelete }) {
  const version = project.draftVersionId
    ? dataEngine.version.get(project.draftVersionId)
    : project.publishedVersionId
      ? dataEngine.version.get(project.publishedVersionId)
      : null;
  const videoCount = version?.videos?.length || 0;
  const totalMin = (version?.videos || []).reduce(
    (s, v) => s + (Number(v.durationMinutes) || 0),
    0
  );

  const canEdit =
    project.state === PROJECT_STATE.BUILDING ||
    project.state === PROJECT_STATE.PUBLISHED;
  const canDelete = project.state !== PROJECT_STATE.UPDATE_PENDING;

  const coverUrl = resolveImageUrl(project.image || project.coverImageBlobId || '');

  return (
    <article className="tb-project-card">
      <div className="tb-project-cover">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={project.title}
            className="tb-project-cover-img"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div
            className="tb-project-cover-placeholder"
            style={{ background: project.color || '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <span style={{ fontSize: 48 }}>🎬</span>
          </div>
        )}
        <div className="tb-project-badges">
          <ProjectStateBadge state={project.state} size="sm" />
        </div>
        {canDelete && (
          <button
            type="button"
            className="tb-project-delete-btn"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            title="حذف کامل"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
              <path
                d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6"
                stroke="currentColor" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
      <div className="tb-project-body">
        <h3 className="tb-project-title">{project.title}</h3>
        <div className="tb-project-subtitle">
          {project.category || 'بدون دسته'} · {project.level || '—'}
        </div>
        <div className="tb-project-stats">
          <StatPill icon="🎬" value={videoCount} label="ویدیو" />
          <StatPill
            icon="⏱️"
            value={totalMin > 0 ? formatDuration(totalMin) : '—'}
            label=""
          />
          <StatPill
            icon="💰"
            value={
              project.fullCoursePrice > 0
                ? formatPrice(project.fullCoursePrice, project.currency)
                : 'رایگان'
            }
            label=""
          />
        </div>
        {project.pendingVersionId && (
          <div className="tb-project-pending">
            <span className="tb-dot tb-dot-warning" />
            نسخه‌ی جدید در انتظار بررسی
          </div>
        )}
      </div>
      <div className="tb-project-actions">
        <button
          className="tb-btn tb-btn-primary tb-btn-block"
          onClick={onEdit}
          disabled={!canEdit}
        >
          {canEdit ? '✏️ ویرایش' : '🔒 قفل شده'}
        </button>
        {project.state === PROJECT_STATE.PUBLISHED && (
          <button
            className="tb-btn tb-btn-ghost tb-btn-block"
            onClick={onPreview}
            style={{ marginTop: 8 }}
          >
            👁️ مشاهده در سایت
          </button>
        )}
      </div>
    </article>
  );
}

function StatPill({ icon, value, label }) {
  return (
    <span className="tb-stat-pill">
      <span className="tb-stat-pill-icon">{icon}</span>
      <span className="tb-stat-pill-value">{value}</span>
      {label && <span className="tb-stat-pill-label">{label}</span>}
    </span>
  );
}

function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return '—';
  if (minutes < 60) return `${toPersianDigits(minutes)} دقیقه`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0
    ? `${toPersianDigits(h)} ساعت`
    : `${toPersianDigits(h)}:${String(m).padStart(2, '0')} ساعت`;
}

// ================================================================
// EDITOR WRAPPER
// ================================================================
function FreeStudyEditor({ projectId, isNew, teacherProfile, onExit }) {
  const navigate = useNavigate();
  const { user } = useBooksEngine();
  const [actualProjectId, setActualProjectId] = useState(projectId || null);
  const [creating, setCreating] = useState(isNew);
  const [createError, setCreateError] = useState(null);
  const creatingRef = useRef(false); // 🆕 StrictMode guard

  useEffect(() => {
    if (!isNew || projectId) return;
    if (creatingRef.current) return;
    creatingRef.current = true;
    try {
      const res = dataEngine.project.create({
        type: 'freeStudy',
        teacherId: teacherProfile.id,
        title: 'دوره‌ی جدید',
        category: '',
        level: 'مقدماتی',
        description: '',
        color: '#4f46e5',
        currency: 'IRT',
        fullCoursePrice: 0,
        state: PROJECT_STATE.BUILDING,
      });
      if (!res.ok) {
        creatingRef.current = false;
        setCreateError(res.errors?.[0] || 'create_failed');
        setCreating(false);
        return;
      }
      const draftRes = dataEngine.version.create({
        projectId: res.data.id,
        parentVersionId: null,
        status: VERSION_STATUS.DRAFT,
        chapters: [],
        videos: [],
        createdBy: user?.id || null,
      });
      if (draftRes.ok) {
        dataEngine.project.setDraft(res.data.id, draftRes.data.id);
      }
      setActualProjectId(res.data.id);
      setCreating(false);
    } catch (e) {
      creatingRef.current = false;
      setCreateError(e.message || 'create_failed');
      setCreating(false);
    }
  }, [isNew, projectId, teacherProfile.id, user?.id]);

  if (creating) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spinner size={32} />
      </div>
    );
  }

  if (createError) {
    return (
      <div style={{ padding: 40 }}>
        <EmptyState
          icon="⚠️"
          title="خطا در ساخت دوره"
          description={createError}
          action={<button className="tb-btn tb-btn-ghost" onClick={onExit}>بازگشت</button>}
        />
      </div>
    );
  }

  if (!actualProjectId) {
    return (
      <div style={{ padding: 40 }}>
        <EmptyState icon="📦" title="دوره یافت نشد" />
      </div>
    );
  }

  return (
    <EditorBody
      projectId={actualProjectId}
      onExit={onExit}
      onPreview={() => navigate(`/free-study/${actualProjectId}`)}
    />
  );
}

// ================================================================
// EDITOR BODY
// ================================================================
function EditorBody({ projectId, onExit, onPreview }) {
  const { toast } = useBooksEngine();
  const editor = useFreeStudyEditor(projectId);
  const { submitForReview } = useVersionActions();

  const [showSubmit, setShowSubmit] = useState(false);
  const [note, setNote] = useState('');
  const [ack, setAck] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const isLocked =
    editor.error === 'locked_pending_review' ||
    editor.project?.state === PROJECT_STATE.UPDATE_PENDING;

  const isPublished = !!editor.project?.publishedVersionId;

  useBeforeUnload(editor.hasUnsaved);

  const shortcuts = useMemo(() => ({
    'ctrl+s': () => { if (editor.hasUnsaved) editor.forceSave(); },
    'mod+s': () => { if (editor.hasUnsaved) editor.forceSave(); },
    'ctrl+z': () => editor.canUndo && editor.undo(),
    'mod+z': () => editor.canUndo && editor.undo(),
    'escape': () => {
      if (showSubmit) setShowSubmit(false);
      else if (showPreview) setShowPreview(false);
    },
  }), [editor, showSubmit, showPreview]);

  useKeyboardShortcuts(shortcuts, { enabled: !isLocked });

  const handleSubmit = useCallback(async () => {
    if (!editor.project) return;

    // 🆕 validate قبل از submit
    const v = dataEngine.validators.validateProject(editor.project);
    if (!v.ok) {
      toast?.('لطفاً اطلاعات دوره را کامل کنید', 'error');
      return;
    }

    const res = await submitForReview.run(projectId, { note });
    if (res?.ok) {
      setShowSubmit(false);
      setNote('');
      setAck(false);
      onExit();
    }
  }, [editor.project, submitForReview, projectId, note, onExit, toast]);

  if (editor.loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spinner size={32} />
      </div>
    );
  }

  if (editor.error === 'project_not_found') {
    return (
      <div style={{ padding: 40 }}>
        <EmptyState
          icon="📦"
          title="دوره یافت نشد"
          action={<button className="tb-btn tb-btn-primary" onClick={onExit}>بازگشت</button>}
        />
      </div>
    );
  }

  const videos = editor.draft?.videos || [];

  return (
    <div style={{ padding: '24px 20px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12, marginBottom: 24,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* 🆕 دکمه بازگشت دایره‌ای برای ویرایشگر */}
          <button
            type="button"
            className="tb-theme-toggle"
            onClick={onExit}
            title="بازگشت"
            aria-label="بازگشت"
            style={{ width: 40, height: 40 }}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.4rem' }}>
              {editor.project?.title || 'دوره‌ی جدید'}
            </h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              <ProjectStateBadge state={editor.project?.state} size="sm" />
              {isPublished && <Badge tone="info" size="sm">منتشرشده</Badge>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <AutoSaveIndicator
            isSaving={editor.isSaving}
            hasUnsaved={editor.hasUnsaved}
            lastSavedAt={editor.lastSavedAt}
            onForceSave={editor.forceSave}
          />
          <button
            className="tb-btn tb-btn-ghost"
            onClick={() => setShowPreview(true)}
            disabled={isLocked}
          >
            👁️ پیش‌نمایش
          </button>
          {!isLocked && (
            <button
              className="tb-btn tb-btn-primary"
              onClick={() => setShowSubmit(true)}
              disabled={videos.length === 0}
            >
              📤 {isPublished ? 'درخواست بروزرسانی' : 'ارسال برای بررسی'}
            </button>
          )}
        </div>
      </div>

      {isLocked && (
        <div style={{
          padding: '12px 16px', background: '#fef3c7', border: '1px solid #fcd34d',
          borderRadius: 12, marginBottom: 16, color: '#92400e',
        }}>
          🔒 نسخه‌ی جدید شما در حال بررسی است. تا زمان تصمیم مدیر، امکان ویرایش وجود ندارد.
        </div>
      )}

      <MetaSection
        project={editor.project}
        onUpdate={editor.updateProjectMeta}
        locked={isLocked}
      />

      <VideosSection
        videos={videos}
        project={editor.project}
        onAdd={editor.addVideo}
        onUpdate={editor.updateVideo}
        onRemove={editor.removeVideo}
        onMove={editor.moveVideo}
        locked={isLocked}
      />

      {/* Submit Modal */}
      <Modal
        isOpen={showSubmit}
        onClose={() => setShowSubmit(false)}
        title={isPublished ? 'ارسال درخواست بروزرسانی' : 'ارسال برای بررسی'}
        size="md"
        closeOnBackdrop={!submitForReview.loading}
        footer={
          <>
            <button
              className="tb-btn tb-btn-ghost"
              onClick={() => setShowSubmit(false)}
              disabled={submitForReview.loading}
            >
              انصراف
            </button>
            <button
              className="tb-btn tb-btn-primary"
              onClick={handleSubmit}
              disabled={submitForReview.loading || (isPublished && !ack)}
            >
              {submitForReview.loading ? <Spinner size={14} /> : '📤'}
              ارسال
            </button>
          </>
        }
      >
        <div style={{ padding: '8px 0' }}>
          <div style={{
            padding: 12, background: '#eff6ff', borderRadius: 10,
            marginBottom: 12, fontSize: '0.9rem', color: '#1e40af',
          }}>
            {isPublished
              ? 'تا زمان بررسی مدیر، دانش‌آموزان نسخه‌ی فعلی را می‌بینند.'
              : 'پس از تایید مدیر، دوره برای همه‌ی دانش‌آموزان قابل مشاهده می‌شود.'}
          </div>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 700, fontSize: '0.85rem' }}>
            توضیح برای مدیر (اختیاری)
          </label>
          <textarea
            className="tb-input"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="مثلاً: ویدیوها را به‌روز کردم..."
            maxLength={1000}
            style={{ width: '100%', padding: 10, borderRadius: 10 }}
          />
          {isPublished && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
              />
              <span style={{ fontSize: '0.85rem' }}>
                از ارسال مطمئنم و می‌دانم تا بررسی مدیر نمی‌توانم ویرایش کنم.
              </span>
            </label>
          )}
        </div>
      </Modal>

      {showPreview && (
        <PreviewModal
          project={editor.project}
          draft={editor.draft}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

// ================================================================
// META SECTION (با debounce و flush)
// ================================================================
function MetaSection({ project, onUpdate, locked }) {
  const { upload: uploadImage, uploading } = useImageUpload();
  const [form, setForm] = useState(() => ({
    title: project?.title || '',
    subtitle: project?.subtitle || '',
    description: project?.description || '',
    category: project?.category || '',
    categoryId: project?.categoryId || null, // 🆕
    level: project?.level || 'مقدماتی',
    tags: Array.isArray(project?.tags) ? [...project.tags] : [],
    color: project?.color || '#4f46e5',
    currency: project?.currency || 'IRT',
    fullCoursePrice: Number(project?.fullCoursePrice) || 0,
    image: project?.image || null,
  }));
  const [pickerOpen, setPickerOpen] = useState(false);
const [categoryList, setCategoryList] = useState(() => {
  try {
    return dataEngine.category?.list() || [];
  } catch { return []; }
});

// 🆕 هر بار که picker باز شد، لیست را از registry بگیر (تازه‌ترین)
useEffect(() => {
  if (!pickerOpen) return;
  try {
    setCategoryList(dataEngine.category?.list() || []);
  } catch {}
}, [pickerOpen]);
  const [tagInput, setTagInput] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0); // 🆕
  const abortControllerRef = useRef(null); // 🆕
  const formRef = useRef(form);
  const saveTimerRef = useRef(null);

  useEffect(() => { formRef.current = form; }, [form]);

  // reset وقتی project عوض شد
  useEffect(() => {
    if (!project) return;
    const next = {
      title: project.title || '',
      subtitle: project.subtitle || '',
      description: project.description || '',
      category: project.category || '',
      categoryId: project.categoryId || null, // 🆕
      level: project.level || 'مقدماتی',
      tags: Array.isArray(project.tags) ? [...project.tags] : [],
      color: project.color || '#4f46e5',
      currency: project.currency || 'IRT',
      fullCoursePrice: Number(project.fullCoursePrice) || 0,
      image: project.image || null,
    };
    setForm(next);
    formRef.current = next;
  }, [project?.id]);

  // flush در unmount
  useEffect(() => () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      try { onUpdate(formRef.current); } catch {}
    }
  }, [onUpdate]);

  // 🆕 debounced apply
  const apply = useCallback((patch) => {
    setForm((f) => {
      const next = { ...f, ...patch };
      formRef.current = next;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        try { onUpdate(formRef.current); } catch {}
      }, 500);
      return next;
    });
  }, [onUpdate]);

  const addTag = () => {
    const v = tagInput.trim();
    if (!v || form.tags.includes(v)) return;
    setTagInput('');
    apply({ tags: [...form.tags, v] });
  };

  const removeTag = (tag) => {
    apply({ tags: form.tags.filter((t) => t !== tag) });
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setUploadProgress(0);
    try {
const res = await uploadImage(file, {
  refType: 'image',
  ownerId: project?.teacherId,
  examId: project?.id,
  onProgress: (p) => setUploadProgress(
    typeof p === 'object' ? (p?.percent ?? 0) : (p ?? 0)
  ),
  signal: controller.signal,
});
      if (res?.blobId) apply({ image: res.blobId });
    } catch (e) {
      if (e.message !== 'upload_aborted' && e.message !== 'server_upload_failed: upload_aborted') {
        // خطای واقعی
      }
    } finally {
      setUploadProgress(0);
      abortControllerRef.current = null;
    }
  };

  const coverUrl = form.image ? resolveImageUrl(form.image) : '';

  return (
    <section style={{
      background: 'var(--tb-card-bg, #ffffff2d)', borderRadius: 16, padding: 20,
      marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    }}>
      <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: '1.1rem' }}>📋 اطلاعات دوره</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <FieldLabel>عنوان دوره *</FieldLabel>
          <input
            className="tb-input"
            value={form.title}
            onChange={(e) => apply({ title: e.target.value })}
            maxLength={200}
            disabled={locked}
            style={inputStyle}
            placeholder="مثلاً: آموزش زبان انگلیسی"
          />
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <FieldLabel>زیرعنوان</FieldLabel>
          <input
            className="tb-input"
            value={form.subtitle}
            onChange={(e) => apply({ subtitle: e.target.value })}
            maxLength={300}
            disabled={locked}
            style={inputStyle}
            placeholder="مثلاً: از صفر تا مکالمه روزمره"
          />
        </div>

<div>
  <FieldLabel>دسته‌بندی</FieldLabel>
  <button
    type="button"
    onClick={() => !locked && setPickerOpen(true)}
    disabled={locked}
    style={{
      ...inputStyle,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      cursor: locked ? 'not-allowed' : 'pointer',
      textAlign: 'right',
      fontFamily: 'inherit',
      fontSize: '0.9rem',
      color: form.category ? '#1e293b' : '#94a3b8',
      background: locked ? '#f8fafc' : '#ffffff',
    }}
  >
    <span>{form.category || 'انتخاب از دسته‌بندی‌های تعریف‌شده...'}</span>
    <span style={{ fontSize: '0.8rem', color: '#64748b' }}>▾</span>
  </button>
</div>

        <div>
          <FieldLabel>سطح</FieldLabel>
          <select
            className="tb-input"
            value={form.level}
            onChange={(e) => apply({ level: e.target.value })}
            disabled={locked}
            style={inputStyle}
          >
            <option value="مقدماتی">مقدماتی</option>
            <option value="متوسط">متوسط</option>
            <option value="پیشرفته">پیشرفته</option>
            <option value="آسان">آسان</option>
            <option value="همه سطوح">همه سطوح</option>
          </select>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <FieldLabel>توضیحات</FieldLabel>
          <textarea
            className="tb-input"
            rows={3}
            value={form.description}
            onChange={(e) => apply({ description: e.target.value })}
            maxLength={5000}
            disabled={locked}
            style={inputStyle}
            placeholder="توضیح کوتاه درباره‌ی دوره..."
          />
        </div>

<div>
  <FieldLabel>🎨 رنگ اختصاصی دوره</FieldLabel>
  <input
    type="color"
    value={form.color}
    onChange={(e) => apply({ color: e.target.value })}
    disabled={locked}
    style={{ width: '100%', height: 42, padding: 4, borderRadius: 10, cursor: 'pointer' }}
  />
  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 6, lineHeight: 1.7 }}>
    این رنگ در صفحه‌ی دوره (هدر، دکمه‌ها و حلقه‌ی پیشرفت) استفاده می‌شه.
  </div>
</div>

        <div>
          <FieldLabel>قیمت کل دوره (تومان)</FieldLabel>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className="tb-input"
            value={form.fullCoursePrice || ''}
            onChange={(e) => {
              const val = e.target.value
                .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                .replace(/[^0-9]/g, '');
              apply({ fullCoursePrice: val ? Number(val) : 0 });
            }}
            disabled={locked}
            style={inputStyle}
            placeholder="0 = رایگان"
          />
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <FieldLabel>تصویر کاور</FieldLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {coverUrl ? (
              <div style={{ position: 'relative' }}>
                <img
                  src={coverUrl}
                  alt="cover"
                  style={{ width: 160, height: 90, objectFit: 'cover', borderRadius: 10 }}
                />
                {!locked && (
                  <button
                    type="button"
                    onClick={() => apply({ image: null })}
                    style={{
                      position: 'absolute', top: -8, right: -8,
                      width: 24, height: 24, borderRadius: '50%',
                      background: '#ef4444', color: '#fff',
                      border: 'none', cursor: 'pointer', fontSize: 12,
                    }}
                  >✕</button>
                )}
              </div>
            ) : (
              <label style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 160, height: 90, border: '2px dashed #cbd5e1',
                borderRadius: 10, cursor: locked ? 'not-allowed' : 'pointer',
                color: '#64748b',
              }}>
                {uploading ? (
                  <CircularProgress progress={uploadProgress} onCancel={() => abortControllerRef.current?.abort()} />
                ) : (
                  '📤 آپلود کاور'
                )}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  disabled={locked || uploading}
                  onChange={(e) => {
                    handleCoverUpload(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <FieldLabel>برچسب‌ها</FieldLabel>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              className="tb-input"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
              disabled={locked}
              placeholder="برچسب + Enter"
              maxLength={50}
              style={{ flex: 1, padding: 8, borderRadius: 10 }}
            />
            <button
              type="button"
              className="tb-btn tb-btn-ghost"
              onClick={addTag}
              disabled={locked || !tagInput.trim()}
            >افزودن</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {form.tags.map((t) => (
              <Chip key={t} tone="purple" onRemove={locked ? null : () => removeTag(t)}>
                {t}
              </Chip>
            ))}
          </div>
        </div>
      </div>
      <CategoryPickerModal
  isOpen={pickerOpen}
  onClose={() => setPickerOpen(false)}
  onSelect={(categoryId, categoryName) => {
    apply({ category: categoryName, categoryId });
  }}
  categories={categoryList}
  currentCategoryId={form.categoryId || null}
  currentCategoryName={form.category || ''}
/>
    </section>
  );
}

const inputStyle = { width: '100%', padding: 10, borderRadius: 10 };
function FieldLabel({ children }) {
  return (
    <label style={{ display: 'block', marginBottom: 4, fontWeight: 700, fontSize: '0.85rem' }}>
      {children}
    </label>
  );
}

// ================================================================
// VIDEOS SECTION
// ================================================================
function VideosSection({
  videos, project, onAdd, onUpdate, onRemove, onMove, locked,
}) {
  return (
    <section style={{
      background: 'var(--tb-card-bg, #ffffff30)', borderRadius: 16, padding: 20,
      marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 16, flexWrap: 'wrap', gap: 8,
      }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>
          🎬 ویدیوهای دوره ({videos.length})
        </h2>
        {!locked && (
          <button className="tb-btn tb-btn-primary" onClick={() => onAdd({})}>
            ➕ افزودن ویدیو
          </button>
        )}
      </div>

      {videos.length === 0 ? (
        <EmptyState
          icon="🎬"
          title="هنوز ویدیویی اضافه نشده"
          description="برای شروع، اولین ویدیوی دوره را اضافه کنید."
          action={
            !locked && (
              <button className="tb-btn tb-btn-primary" onClick={() => onAdd({})}>
                ➕ افزودن اولین ویدیو
              </button>
            )
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {videos.map((video, idx) => (
            <VideoRow
              key={video._id}
              video={video}
              index={idx}
              total={videos.length}
              project={project}
              locked={locked}
              onUpdate={(patch) => onUpdate(video._id, patch)}
              onRemove={() => onRemove(video._id)}
              onMoveUp={() => onMove(video._id, 'up')}
              onMoveDown={() => onMove(video._id, 'down')}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function VideoRow({
  video, index, total, project, locked,
  onUpdate, onRemove, onMoveUp, onMoveDown,
}) {
  const [expanded, setExpanded] = useState(!video.blobId);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 🆕
  const abortControllerRef = useRef(null); // 🆕
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [error, setError] = useState(null);
  const [durationText, setDurationText] = useState(String(video.durationMinutes ?? 0));
  const [resolvedVideoUrl, setResolvedVideoUrl] = useState(null);

  useEffect(() => {
    setDurationText(String(video.durationMinutes ?? 0));
  }, [video.durationMinutes]);

  useEffect(() => {
    if (!video.blobId) { setResolvedVideoUrl(null); return; }
    let cancelled = false;
    engine.blobs.getUrl(video.blobId).then((u) => {
      if (!cancelled) setResolvedVideoUrl(u);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [video.blobId]);

  const handleVideoUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      setError('فقط فایل ویدیویی مجاز است');
      return;
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    try {
const res = await engine.blobs.save(file, {
  refType: 'video',
  ownerId: project.teacherId,
  examId: project.id,
  filename: file.name,
  mimeType: file.type,
  onProgress: (p) => setUploadProgress(
    typeof p === 'object' ? (p?.percent ?? 0) : (p ?? 0)
  ),
  signal: controller.signal,
});
      onUpdate({ blobId: res.blobId, size: res.size });
    } catch (e) {
      if (e.message !== 'upload_aborted' && !e.message?.includes('upload_aborted')) {
        setError(e.message || 'خطا در آپلود');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
      abortControllerRef.current = null;
    }
  };

const handleThumbUpload = async (file) => {
  if (!file) return;
  if (!file.type.startsWith('image/')) return;
  setUploadingThumb(true);
  setUploadProgress(0);
  try {
    const res = await engine.blobs.save(file, {
      refType: 'image',
      ownerId: project.teacherId,
      examId: project.id,
      filename: file.name,
      mimeType: file.type,
      onProgress: (p) => setUploadProgress(
        typeof p === 'object' ? (p?.percent ?? 0) : (p ?? 0)
      ),
    });
    onUpdate({ thumbnailBlobId: res.blobId });
  } catch (e) {
    setError(e.message || 'خطا در آپلود');
  } finally {
    setUploadingThumb(false);
    setUploadProgress(0);
  }
};

  const commitDuration = () => {
    const num = parseInt(durationText, 10);
    const clamped = Math.max(0, Math.min(600, isNaN(num) ? 0 : num));
    setDurationText(String(clamped));
    if (video.durationMinutes !== clamped) onUpdate({ durationMinutes: clamped });
  };

  const thumbUrl = video.thumbnailBlobId ? resolveImageUrl(video.thumbnailBlobId) : '';

  return (
    <div style={{
      border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden',
      background: video.blobId ? '#ffffff27' : '#f8fafc1d',
    }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 14px', background: '#f1f5f937', cursor: 'pointer',
        }}
        onClick={() => setExpanded((e) => !e)}
      >
        <span style={{ fontWeight: 700, color: '#64748b', minWidth: 24 }}>
          {toPersianDigits(index + 1)}
        </span>
        <span style={{ flex: 1, fontWeight: 700, fontSize: '0.9rem' }}>
          {video.title || 'ویدیوی جدید'}
        </span>
        {video.isFree && (
          <span style={{
            fontSize: 11, padding: '2px 8px',
            background: '#10b981', color: '#fff', borderRadius: 999,
          }}>رایگان</span>
        )}
        {!video.isFree && video.price > 0 && (
          <span style={{
            fontSize: 11, padding: '2px 8px',
            background: '#4f46e5', color: '#fff', borderRadius: 999,
          }}>{formatPrice(video.price, project.currency)}</span>
        )}
        {!video.blobId && (
          <span style={{ fontSize: 11, color: '#ef4444' }}>⚠️ بدون فایل</span>
        )}
        {!locked && (
          <div style={{ display: 'flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
            <button type="button" className="tb-icon-btn" onClick={onMoveUp} disabled={index === 0} title="بالا">↑</button>
            <button type="button" className="tb-icon-btn" onClick={onMoveDown} disabled={index === total - 1} title="پایین">↓</button>
            <button type="button" className="tb-icon-btn" onClick={onRemove} title="حذف" style={{ color: '#ef4444' }}>✕</button>
          </div>
        )}
      </div>

      {expanded && (
        <div style={{ padding: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <FieldLabel>عنوان ویدیو *</FieldLabel>
              <input
                className="tb-input"
                value={video.title || ''}
                onChange={(e) => onUpdate({ title: e.target.value })}
                maxLength={200}
                disabled={locked}
                style={{ width: '100%', padding: 8, borderRadius: 8 }}
                placeholder="مثلاً: درس ۱: آشنایی با الفبا"
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <FieldLabel>توضیح</FieldLabel>
              <textarea
                className="tb-input"
                rows={2}
                value={video.description || ''}
                onChange={(e) => onUpdate({ description: e.target.value })}
                maxLength={2000}
                disabled={locked}
                style={{ width: '100%', padding: 8, borderRadius: 8 }}
              />
            </div>

            <div>
              <FieldLabel>مدت (دقیقه)</FieldLabel>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="tb-input"
                value={durationText}
                onChange={(e) => {
                  const val = e.target.value
                    .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                    .replace(/[^0-9]/g, '');
                  setDurationText(val);
                }}
                onBlur={() => commitDuration(durationText)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                disabled={locked}
                style={{ width: '100%', padding: 8, borderRadius: 8 }}
                placeholder="0"
              />
            </div>

            <div>
              <FieldLabel>قیمت (تومان)</FieldLabel>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="tb-input"
                value={video.price || ''}
                onChange={(e) => {
                  const val = e.target.value
                    .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                    .replace(/[^0-9]/g, '');
                  onUpdate({ price: val ? Number(val) : 0 });
                }}
                disabled={locked || video.isFree}
                style={{ width: '100%', padding: 8, borderRadius: 8 }}
              />
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 8,
                cursor: locked ? 'not-allowed' : 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={video.isFree === true}
                  onChange={(e) => onUpdate({
                    isFree: e.target.checked,
                    price: e.target.checked ? 0 : video.price,
                  })}
                  disabled={locked}
                />
                <span style={{ fontSize: '0.85rem' }}>🎁 این ویدیو رایگان است</span>
              </label>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <FieldLabel>فایل ویدیو *</FieldLabel>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {video.blobId ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ color: '#22c55e' }}>✓ آپلود شده</span>
                    {resolvedVideoUrl && (
                      <video
                        src={resolvedVideoUrl}
                        controls
                        preload="metadata"
                        style={{ width: 220, height: 124, borderRadius: 8, background: '#000' }}
                      />
                    )}
                    {!locked && (
                      <button
                        type="button"
                        className="tb-btn tb-btn-ghost"
                        onClick={() => onUpdate({ blobId: null })}
                        style={{ color: '#ef4444' }}
                      >حذف فایل</button>
                    )}
                  </div>
                ) : (
                  <label style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: 16, border: '2px dashed #cbd5e1', borderRadius: 8,
                    cursor: locked ? 'not-allowed' : 'pointer', color: '#64748b',
                  }}>
                    {uploading ? (
                      <CircularProgress progress={uploadProgress} onCancel={() => abortControllerRef.current?.abort()} />
                    ) : (
                      '📤 آپلود فایل ویدیو'
                    )}
                    <input
                      type="file"
                      accept="video/*"
                      style={{ display: 'none' }}
                      disabled={locked || uploading}
                      onChange={(e) => {
                        handleVideoUpload(e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>
            </div>

{SHOW_THUMBNAIL_UPLOAD && (
  <div style={{ gridColumn: '1 / -1' }}>
    <FieldLabel>تصویر بندانگشتی (اختیاری)</FieldLabel>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {thumbUrl && (
        <img
          src={thumbUrl}
          alt="thumb"
          style={{ width: 120, height: 68, objectFit: 'cover', borderRadius: 8 }}
        />
      )}
      {!locked && (
        <label style={{
          padding: '6px 14px', border: '1px dashed #cbd5e1',
          borderRadius: 8, cursor: 'pointer',
          fontSize: '0.8rem', color: '#64748b',
        }}>
          {uploadingThumb ? <Spinner size={14} /> : '📷 آپلود تصویر'}
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            disabled={locked || uploadingThumb}
            onChange={(e) => {
              handleThumbUpload(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
      )}
      {video.thumbnailBlobId && !locked && (
        <button
          type="button"
          className="tb-btn tb-btn-ghost"
          onClick={() => onUpdate({ thumbnailBlobId: null })}
          style={{ fontSize: '0.75rem', color: '#ef4444' }}
        >حذف</button>
      )}
    </div>
  </div>
)}

            {error && (
              <div style={{ gridColumn: '1 / -1', color: '#ef4444', fontSize: '0.8rem' }}>
                ⚠️ {error}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ================================================================
// PREVIEW MODAL
// ================================================================
function PreviewModal({ project, draft, onClose }) {
  const videos = draft?.videos || [];
  const totalMin = videos.reduce((s, v) => s + (Number(v.durationMinutes) || 0), 0);
  const coverUrl = resolveImageUrl(project.image || project.coverImageBlobId || '');

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: '#fff', borderRadius: 16,
        maxWidth: 700, width: '100%', maxHeight: '90vh',
        overflow: 'auto', padding: 24,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>👁️ پیش‌نمایش دوره</h2>
          <button className="tb-btn tb-btn-ghost" onClick={onClose}>✕</button>
        </div>

        <div style={{
          padding: 20, borderRadius: 12,
          background: `linear-gradient(135deg, ${project.color || '#4f46e5'}, ${project.color || '#4f46e5'}cc)`,
          color: '#fff', marginBottom: 16,
        }}>
          {coverUrl && (
            <img src={coverUrl} alt="cover" style={{
              width: '100%', height: 160, objectFit: 'cover',
              borderRadius: 8, marginBottom: 12, opacity: 0.85,
            }} />
          )}
          <h1 style={{ margin: 0, marginBottom: 8 }}>{project.title || 'بدون عنوان'}</h1>
          {project.subtitle && <p style={{ margin: 0, opacity: 0.9 }}>{project.subtitle}</p>}
          <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: '0.85rem', opacity: 0.9, flexWrap: 'wrap' }}>
            <span>🎬 {videos.length} ویدیو</span>
            <span>⏱️ {formatDuration(totalMin)}</span>
            <span>💰 {project.fullCoursePrice > 0 ? formatPrice(project.fullCoursePrice, project.currency) : 'رایگان'}</span>
          </div>
        </div>

        {project.description && (
          <p style={{ color: '#475569', lineHeight: 1.8 }}>{project.description}</p>
        )}

        <h3 style={{ marginTop: 16 }}>ویدیوها</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {videos.map((v, i) => (
            <div key={v._id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: 10, background: '#f8fafc', borderRadius: 8,
            }}>
              <span style={{ fontWeight: 700, color: '#64748b' }}>{toPersianDigits(i + 1)}</span>
              <span style={{ flex: 1 }}>{v.title}</span>
              {v.isFree && <span style={{ fontSize: 11, color: '#10b981', fontWeight: 700 }}>رایگان</span>}
              {!v.isFree && v.price > 0 && (
                <span style={{ fontSize: 11, color: '#4f46e5', fontWeight: 700 }}>
                  {formatPrice(v.price, project.currency)}
                </span>
              )}
            </div>
          ))}
        </div>

        <div style={{ marginTop: 20, padding: 12, background: '#fef3c7', borderRadius: 8, fontSize: '0.85rem', color: '#92400e' }}>
          ℹ️ این پیش‌نمایش است. دانش‌آموزان پس از تایید مدیر آن را می‌بینند.
        </div>
      </div>
    </div>
  );
}